import React, { useEffect, useState } from 'react'
import {Shield, User, Crown, Search, AlertTriangle, Edit, Trash2, Key, UserX, UserCheck} from 'lucide-react'
import { useEntityCRUD } from '../../hooks/useEntityCRUD'
import { useAdminLog } from '../../hooks/useAdminLog'
import { lumi } from '../../lib/lumi'
import AdminLayout from '../../components/admin/AdminLayout'
import toast from 'react-hot-toast'

interface EditUserForm {
  name: string
  email: string
  phone: string
}

const UsersManagement: React.FC = () => {
  const { loading, updateRecord, deleteRecord } = useEntityCRUD('users')
  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const { logAction } = useAdminLog()
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('ALL')
  const [currentUserRole, setCurrentUserRole] = useState<string>('')
  const [editingUser, setEditingUser] = useState<any>(null)
  const [editForm, setEditForm] = useState<EditUserForm>({ name: '', email: '', phone: '' })
  const currentUser = lumi.auth.user

  useEffect(() => {
    loadUsers()
    loadCurrentUserRole()
  }, [roleFilter])

  const loadUsers = async () => {
    try {
      // Buscar todos os usuários
      const { list } = await lumi.entities.users.list({
        sort: { createdAt: -1 }
      })

      // Garantir que todos tenham role (usuários antigos podem não ter)
      const usersWithRole = await Promise.all(
        list.map(async (user: any) => {
          if (!user.role) {
            // Atualizar usuários sem role para USER
            try {
              await lumi.entities.users.update(user._id, { 
                role: 'USER',
                active: user.active ?? true,
                updatedAt: new Date().toISOString() 
              })
              return { ...user, role: 'USER', active: user.active ?? true }
            } catch (error) {
              console.error('Erro ao atualizar role do usuário:', user._id, error)
              return { ...user, role: 'USER', active: user.active ?? true }
            }
          }
          return user
        })
      )

      // Aplicar filtro de role se necessário
      const filtered = roleFilter !== 'ALL' 
        ? usersWithRole.filter((u: any) => u.role === roleFilter)
        : usersWithRole

      setData(filtered)
      setTotal(filtered.length)
    } catch (error) {
      console.error('Erro ao carregar usuários:', error)
      toast.error('Erro ao carregar usuários')
    }
  }

  const loadCurrentUserRole = async () => {
    if (!currentUser) return
    
    try {
      const { list } = await lumi.entities.users.list({
        filter: { email: currentUser.email }
      })
      if (list.length > 0) {
        setCurrentUserRole(list[0].role)
      }
    } catch (error) {
      console.error('Erro ao carregar role do usuário:', error)
    }
  }

  const isAdminMaster = currentUserRole === 'ADMIN_MASTER'

  const handlePromoteToAdmin = async (userId: string, userName: string) => {
    if (!isAdminMaster) {
      toast.error('Apenas Admin Master pode promover usuários!')
      return
    }

    if (!confirm(`Tem certeza que deseja promover ${userName} a Administrador?`)) return

    try {
      await updateRecord(userId, { role: 'ADMIN', updatedAt: new Date().toISOString() })
      await logAction('PROMOTE', 'users', userId, `Usuário promovido a ADMIN: ${userName}`)
      toast.success(`${userName} promovido a Administrador!`)
      loadUsers()
    } catch (error) {
      console.error('Falha ao promover usuário:', error)
      toast.error('Erro ao promover usuário')
    }
  }

  const handleDemoteToUser = async (userId: string, userName: string, userRole: string) => {
    if (!isAdminMaster) {
      toast.error('Apenas Admin Master pode rebaixar administradores!')
      return
    }

    // Impedir rebaixamento de ADMIN_MASTER
    if (userRole === 'ADMIN_MASTER') {
      toast.error('Não é possível rebaixar um Admin Master!')
      return
    }

    // Verificar auto-rebaixamento
    if (currentUser?.userId === userId) {
      toast.error('Você não pode rebaixar sua própria conta!')
      return
    }

    if (!confirm(`Tem certeza que deseja rebaixar ${userName} para Usuário?`)) return

    try {
      await updateRecord(userId, { role: 'USER', updatedAt: new Date().toISOString() })
      await logAction('DEMOTE', 'users', userId, `Admin rebaixado para USER: ${userName}`)
      toast.success(`${userName} rebaixado para Usuário!`)
      loadUsers()
    } catch (error) {
      console.error('Falha ao rebaixar admin:', error)
      toast.error('Erro ao rebaixar administrador')
    }
  }

  const handleToggleActive = async (userId: string, userName: string, currentActive: boolean) => {
    if (!isAdminMaster) {
      toast.error('Apenas Admin Master pode ativar/desativar usuários!')
      return
    }

    // Impedir desativação própria
    if (currentUser?.userId === userId) {
      toast.error('Você não pode desativar sua própria conta!')
      return
    }

    const action = currentActive ? 'desativar' : 'ativar'
    if (!confirm(`Tem certeza que deseja ${action} ${userName}?`)) return

    try {
      await updateRecord(userId, { active: !currentActive, updatedAt: new Date().toISOString() })
      await logAction(currentActive ? 'DEACTIVATE' : 'ACTIVATE', 'users', userId, `Usuário ${action}do: ${userName}`)
      toast.success(`${userName} ${action}do com sucesso!`)
      loadUsers()
    } catch (error) {
      console.error(`Falha ao ${action} usuário:`, error)
      toast.error(`Erro ao ${action} usuário`)
    }
  }

  const handleDeleteUser = async (userId: string, userName: string, userRole: string) => {
    if (!isAdminMaster) {
      toast.error('Apenas Admin Master pode excluir usuários!')
      return
    }

    // Impedir exclusão de ADMIN_MASTER
    if (userRole === 'ADMIN_MASTER') {
      toast.error('Não é possível excluir um Admin Master!')
      return
    }

    // Verificar auto-exclusão
    if (currentUser?.userId === userId) {
      toast.error('Você não pode excluir sua própria conta!')
      return
    }

    if (!confirm(`⚠️ ATENÇÃO: Tem certeza que deseja EXCLUIR permanentemente ${userName}?\n\nEsta ação não pode ser desfeita!`)) return

    try {
      await deleteRecord(userId)
      await logAction('DELETE', 'users', userId, `Usuário excluído: ${userName}`)
      toast.success(`${userName} excluído com sucesso!`)
      loadUsers()
    } catch (error) {
      console.error('Falha ao excluir usuário:', error)
      toast.error('Erro ao excluir usuário')
    }
  }

  const handleEditUser = (user: any) => {
    setEditingUser(user)
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || ''
    })
  }

  const handleSaveEdit = async () => {
    if (!isAdminMaster) {
      toast.error('Apenas Admin Master pode editar usuários!')
      return
    }

    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast.error('Nome e email são obrigatórios!')
      return
    }

    try {
      await updateRecord(editingUser._id, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim(),
        updatedAt: new Date().toISOString()
      })
      await logAction('UPDATE', 'users', editingUser._id, `Dados do usuário atualizados: ${editForm.name}`)
      toast.success('Usuário atualizado com sucesso!')
      setEditingUser(null)
      loadUsers()
    } catch (error) {
      console.error('Falha ao atualizar usuário:', error)
      toast.error('Erro ao atualizar usuário')
    }
  }

  const handleChangeMyPassword = async () => {
    try {
      toast('Abrindo o portal de autenticação para troca de senha...')
      await lumi.auth.signOut()
      await lumi.auth.signIn()
      toast.success('No portal de login, use a opção "Esqueci minha senha" para definir uma nova senha.')
    } catch (error) {
      console.error('Falha ao abrir fluxo de troca de senha:', error)
      toast.error('Não foi possível iniciar o fluxo de troca de senha')
    }
  }

  const handleResetPassword = async (userEmail: string) => {
    if (!isAdminMaster) {
      toast.error('Apenas Admin Master pode solicitar reset de senha!')
      return
    }

    toast.error(`Por segurança, o reset de senha deve ser feito pelo próprio usuário no portal de login (email: ${userEmail}).`)
  }

  const filteredUsers = data.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'ADMIN_MASTER':
        return { icon: Crown, text: 'Admin Master', bg: 'bg-yellow-100', color: 'text-yellow-800' }
      case 'ADMIN':
        return { icon: Shield, text: 'Administrador', bg: 'bg-purple-100', color: 'text-purple-800' }
      default:
        return { icon: User, text: 'Usuário', bg: 'bg-gray-100', color: 'text-gray-800' }
    }
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Gerenciar Usuários</h1>
            <p className="text-gray-600">
              {isAdminMaster 
                ? 'Gerencie permissões, edite dados e controle acessos (Admin Master)'
                : 'Visualize usuários do sistema (somente leitura)'}
            </p>
          </div>

          <button
            onClick={handleChangeMyPassword}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black transition-colors"
            title="Trocar minha senha"
          >
            <Key size={18} />
            Trocar minha senha
          </button>
        </div>
      </div>

      {!isAdminMaster && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="text-blue-500 mr-3 mt-0.5" size={20} />
            <div>
              <h3 className="text-sm font-semibold text-blue-800">Acesso Limitado</h3>
              <p className="text-sm text-blue-700 mt-1">
                Você possui permissões de visualização. Apenas Admin Master pode gerenciar usuários.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
          >
            <option value="ALL">Todos os Roles</option>
            <option value="ADMIN_MASTER">Admin Master</option>
            <option value="ADMIN">Administradores</option>
            <option value="USER">Usuários</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredUsers.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              Nenhum usuário encontrado
            </div>
          ) : (
            filteredUsers.map((user: any) => {
              const roleBadge = getRoleBadge(user.role)
              const RoleIcon = roleBadge.icon
              const isCurrentUser = currentUser?.email === user.email

              return (
                <div key={user._id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <div className={`p-2 rounded-full ${roleBadge.bg}`}>
                          <RoleIcon className={roleBadge.color} size={20} />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{user.name}</h3>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${roleBadge.bg} ${roleBadge.color}`}>
                          {roleBadge.text}
                        </span>
                        {isCurrentUser && (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                            Você
                          </span>
                        )}
                        {!user.active && (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                            Desativado
                          </span>
                        )}
                      </div>
                      {user.phone && (
                        <p className="text-sm text-gray-600 ml-12">{user.phone}</p>
                      )}
                      <p className="text-xs text-gray-400 ml-12 mt-1">
                        Cadastrado em: {new Date(user.createdAt).toLocaleString('pt-BR')}
                      </p>
                    </div>

                    {isAdminMaster && !isCurrentUser && (
                      <div className="flex flex-wrap gap-2 ml-4">
                        {user.role === 'USER' && (
                          <button
                            onClick={() => handlePromoteToAdmin(user._id, user.name)}
                            className="flex items-center gap-1 px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
                            title="Promover a Administrador"
                          >
                            <Shield size={16} /> Promover
                          </button>
                        )}
                        
                        {user.role === 'ADMIN' && (
                          <button
                            onClick={() => handleDemoteToUser(user._id, user.name, user.role)}
                            className="flex items-center gap-1 px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm"
                            title="Rebaixar para Usuário"
                          >
                            <User size={16} /> Rebaixar
                          </button>
                        )}

                        <button
                          onClick={() => handleEditUser(user)}
                          className="flex items-center gap-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                          title="Editar Usuário"
                        >
                          <Edit size={16} /> Editar
                        </button>

                        <button
                          onClick={() => handleResetPassword(user.email)}
                          className="flex items-center gap-1 px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
                          title="Reset de senha (o usuário deve concluir no portal de login)"
                        >
                          <Key size={16} /> Reset senha
                        </button>

                        <button
                          onClick={() => handleToggleActive(user._id, user.name, user.active)}
                          className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm ${
                            user.active 
                              ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                          title={user.active ? 'Desativar Usuário' : 'Ativar Usuário'}
                        >
                          {user.active ? <UserX size={16} /> : <UserCheck size={16} />}
                          {user.active ? 'Desativar' : 'Ativar'}
                        </button>

                        {user.role !== 'ADMIN_MASTER' && (
                          <button
                            onClick={() => handleDeleteUser(user._id, user.name, user.role)}
                            className="flex items-center gap-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                            title="Excluir Usuário"
                          >
                            <Trash2 size={16} /> Excluir
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}

      {/* Modal de Edição */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Editar Usuário</h2>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                  placeholder="Nome completo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                  placeholder="email@exemplo.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSaveEdit}
                className="flex-1 bg-[#1e3a8a] text-white px-4 py-2 rounded-lg hover:bg-[#2d4ba5] transition-colors"
              >
                Salvar Alterações
              </button>
              <button
                onClick={() => setEditingUser(null)}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default UsersManagement
